import { Content, Flex, Grid, Heading, IllustratedMessage, minmax, repeat } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import NoSearchResults from '@spectrum-icons/illustrations/NoSearchResults';
import qs from 'query-string';
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CloudAppCard, ErrorBoundary, Pagination } from '@exchange-frontends/components';
import { useAnalyticsPageLoad, useMediaQuery, useStaticData } from '@exchange-frontends/custom-hooks';
import { browsePage } from '@exchange-frontends/utils';

import { useStore } from '../../store';
import { useSearchResults } from '../../utils/hooks';
import AppCardError from './AppCardError';
import SortElement from './SortElement';
import TotalResults from './TotalResults';

const SearchResults = ({ filterKeys }) => {
  const { matchedBreakpoints } = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();
  const cloud = useStore((state) => state.cloud);
  const page = useMemo(() => Number(qs.parse(location.search).page ?? '1'), [location.search]);
  const loading = useStore((state) => state.loading);
  const isTabletSize = !matchedBreakpoints.includes('L');
  const productsLocs = useStaticData('hostApps');
  const twoColumns = useMediaQuery('(min-width: 1025px) and (max-width:1265px)');
  const { product = '' } = qs.parse(location.search);
  const { apps, totalApps, totalPages, encounteredError } = useSearchResults(location.search, filterKeys, page, cloud);

  const onPageChange = (page) => {
    useStore.setState({ loading: true });
    navigate({
      search: qs.stringify(Object.assign({}, qs.parse(location.search), { page })),
    });
  };

  useAnalyticsPageLoad(browsePage, !loading, totalApps);

  return (
    <Flex direction="column" alignItems={'stretch'}>
      <Flex justifyContent="space-between" alignItems="center" width="100%">
        {apps && <TotalResults loading={loading} totalApps={totalApps} productsLocs={productsLocs} />}
        {!isTabletSize && apps?.length !== 0 && <SortElement mobileVersion={false} width="140px" />}
      </Flex>
      <ErrorBoundary isError={encounteredError}>
        {() => (
          <Grid
            gap="size-200"
            columns={repeat('auto-fill', minmax('min(100%, max(270px, 100%/3.5))', '1fr'))}
            justifyContent={twoColumns ? 'space-between' : null}
          >
            {apps?.map((app, idx) => (
              <ErrorBoundary
                key={app ? `${app.id}.${app.name}.${app.type}` : `eb${idx}`}
                errorComponent={<AppCardError appId={app?.id} />}
              >
                <CloudAppCard
                  cloud={cloud}
                  app={app}
                  loading={loading}
                  productsLocs={productsLocs}
                  product={product}
                  headerId="Search Results"
                  appType={app?.appType}
                  publisher={app?.publisher}
                />
              </ErrorBoundary>
            ))}
          </Grid>
        )}
      </ErrorBoundary>
      {totalPages > 1 && !encounteredError && (
        <Pagination
          defaultPage={page}
          totalPages={totalPages}
          enablePageNumbers={matchedBreakpoints.includes('S') ? true : false}
          onChange={onPageChange}
        />
      )}
      {apps?.length === 0 && !loading && (
        <IllustratedMessage>
          <NoSearchResults />
          <Heading>No results found</Heading>
          <Content>Check your spelling or try broadening your search.</Content>
        </IllustratedMessage>
      )}
    </Flex>
  );
};

export default SearchResults;
